import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientiApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, MoreVertical, Eye, Trash2, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const ClientiPage = () => {
  const navigate = useNavigate();
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [newCliente, setNewCliente] = useState({ denominazione_sociale: '', comune: '', provincia: '' });

  const fetchClienti = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clientiApi.getAll(searchQuery);
      setClienti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchClienti(), 300);
    return () => clearTimeout(debounce);
  }, [fetchClienti]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCliente.denominazione_sociale.trim()) { toast.error('La denominazione sociale è obbligatoria'); return; }
    setCreateLoading(true);
    try {
      const response = await clientiApi.create(newCliente);
      toast.success(`Cliente creato con codice ${response.data.codice}`);
      setShowCreateDialog(false);
      setNewCliente({ denominazione_sociale: '', comune: '', provincia: '' });
      navigate(`/clienti/${response.data.id}`);
    } catch (error) {
      toast.error('Errore nella creazione');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCliente) return;
    setDeleteLoading(true);
    try {
      await clientiApi.delete(selectedCliente.id);
      toast.success('Cliente eliminato');
      setShowDeleteDialog(false);
      setSelectedCliente(null);
      fetchClienti();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="clienti-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-slate-900 tracking-tight">Clienti</h1>
          <p className="text-slate-500 mt-1">Gestisci i committenti delle campagne pubblicitarie</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-cliente-btn" className="bg-primary hover:bg-primary/90">
          <Plus size={18} className="mr-2" />Nuovo Cliente
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cerca per codice, denominazione o comune..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-clienti-input" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="clienti-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <Users size={20} className="text-primary" />Lista Clienti ({clienti.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</div>
          ) : clienti.length === 0 ? (
            <div className="text-center py-12 empty-state rounded-lg">
              <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nessun cliente trovato</h3>
              <p className="text-slate-500 mb-4">{searchQuery ? 'Prova a modificare i criteri di ricerca' : 'Inizia aggiungendo il primo cliente'}</p>
              {!searchQuery && <Button onClick={() => setShowCreateDialog(true)}><Plus size={18} className="mr-2" />Aggiungi Cliente</Button>}
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-[100px]">Codice</TableHead>
                    <TableHead className="font-semibold">Denominazione Sociale</TableHead>
                    <TableHead className="font-semibold">Comune</TableHead>
                    <TableHead className="font-semibold">Provincia</TableHead>
                    <TableHead className="font-semibold">Referente</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clienti.map((cliente) => (
                    <TableRow key={cliente.id} className="table-row-animate cursor-pointer" onClick={() => navigate(`/clienti/${cliente.id}`)} data-testid={`cliente-row-${cliente.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{cliente.codice}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{cliente.denominazione_sociale}</TableCell>
                      <TableCell>{cliente.comune || '-'}</TableCell>
                      <TableCell>{cliente.provincia || '-'}</TableCell>
                      <TableCell>{cliente.referente1_nome || '-'}</TableCell>
                      <TableCell>{cliente.email || '-'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical size={16} /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/clienti/${cliente.id}`)}><Eye className="mr-2 h-4 w-4" />Visualizza</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => { setSelectedCliente(cliente); setShowDeleteDialog(true); }}><Trash2 className="mr-2 h-4 w-4" />Elimina</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Nuovo Cliente</DialogTitle>
            <DialogDescription>Inserisci i dati del committente. Il codice verrà assegnato automaticamente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="denominazione">Denominazione Sociale *</Label>
                <Input id="denominazione" value={newCliente.denominazione_sociale} onChange={(e) => setNewCliente({ ...newCliente, denominazione_sociale: e.target.value })} placeholder="Es: Azienda ABC S.r.l." data-testid="input-denominazione" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="comune">Comune</Label>
                  <Input id="comune" value={newCliente.comune} onChange={(e) => setNewCliente({ ...newCliente, comune: e.target.value })} placeholder="Es: Roma" data-testid="input-comune" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provincia">Provincia</Label>
                  <Input id="provincia" value={newCliente.provincia} onChange={(e) => setNewCliente({ ...newCliente, provincia: e.target.value })} placeholder="Es: RM" data-testid="input-provincia" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={createLoading} data-testid="submit-create-cliente">{createLoading ? 'Creazione...' : 'Crea Cliente'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-600">Elimina Cliente</DialogTitle>
            <DialogDescription>Sei sicuro di voler eliminare <strong>{selectedCliente?.codice} - {selectedCliente?.denominazione_sociale}</strong>?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>{deleteLoading ? 'Eliminazione...' : 'Elimina'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientiPage;
